/**
 * ConfiggrManager.ts - Core configuration and preferences management
 * 
 * CHANGE HISTORY:
 * - 251102_1720 danielbeer: Enhanced Profylr integration with profile-based settings
 * - 251102_1633 danielbeer: Added Profylr and Interfacr integration
 * - 251023_1840 [pending] danielbeer: Add Actr integration and enhanced core
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 * 
 * ARCHITECTURE NOTE: Configgr uses HYBRID pattern
 * 
 * Why Hybrid:
 * 1. Configuration State: Settings and modules are persisted
 * 2. Dynamic Operations: Runtime configuration changes and module registration
 * 3. Entity Management: Modules, settings, and themes tracked
 * 
 * Pattern: Hybrid (Actions + CRUD)
 * - Actions: For configuration operations (get, update, register, export, import)
 * - CRUD: For module, setting, and theme persistence
 * 
 * INTERFACR INTEGRATION:
 * - Routes UI operations through Interfacr instead of direct package usage
 * - Receives profile context for adaptive UI
 * 
 * PROFYLR INTEGRATION:
 * - Supports profile-based settings (different settings per profile)
 * - Applies profile perspective to preferences UI
 */

// ModuleRegistry is now in @opcentral/interfacr
// We'll receive it as a parameter or use the singleton from interfacr
// TODO: Create SettingsHierarchy
// import { SettingsHierarchy } from '../components/PreferencesManager/SettingsHierarchy';

interface ConfiggrManagerOptions {
  actr?: any;
  cachr?: any;
  loggr?: any;
  interfacr?: any;
  profylr?: any;
  appId?: string;
  storage?: Storage;
  moduleRegistry?: any; // ModuleRegistry singleton from interfacr
}

export class ConfiggrManager {
  private actr: any;
  private cachr: any;
  private loggr: any;
  private interfacr: any;
  private profylr: any;
  private appId: string;
  private moduleRegistry: any;
  private settingsHierarchy: any;
  
  constructor(options: ConfiggrManagerOptions = {}) {
    this.actr = options.actr;
    this.cachr = options.cachr;
    this.loggr = options.loggr;
    this.interfacr = options.interfacr;
    this.profylr = options.profylr;
    this.appId = options.appId || 'default';
    
    // Use moduleRegistry from options (passed from interfacr)
    this.moduleRegistry = options.moduleRegistry || { 
      getGroupedModules: () => ({ general: [], modules: [], views: [], styles: [], advanced: [] }),
      registerModule: () => {},
      getAllModules: () => []
    };
    
    // Initialize settingsHierarchy with localStorage-based implementation
    this.settingsHierarchy = this.createSettingsHierarchy(options.storage);
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
    
    // Register with Interfacr if available
    if (this.interfacr) {
      this.registerWithInterfacr();
    }
    
    // Log initialization
    if (this.loggr) {
      this.loggr.log('info', `ConfiggrManager initialized for app: ${this.appId}`);
    }
  }
  
  /**
   * Register Configgr actions with Actr
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // Get setting action
    this.actr.register('configgr:get-setting', {
      handler: async (params: any) => {
        return this.getSetting(params.path, params.viewId, params.datasetId);
      }
    });
    
    // Update setting action
    this.actr.register('configgr:update-setting', {
      handler: async (params: any) => {
        return this.updateSetting(params.path, params.value, params.level, params.viewId, params.datasetId);
      }
    });
    
    // Register module action
    this.actr.register('configgr:register-module', {
      handler: async (params: any) => {
        return this.registerModule(params.module);
      }
    });
    
    // Export settings action
    this.actr.register('configgr:export-settings', {
      handler: async (params: any) => {
        return this.exportSettings();
      }
    });
    
    // Import settings action
    this.actr.register('configgr:import-settings', {
      handler: async (params: any) => {
        return this.importSettings(params.settings);
      }
    });
    
    // TODO: CRUD operations disabled - registerCRUD method doesn't exist in current Actr API
    // Need to implement these as individual register() calls or update Actr to support CRUD
    /*
    // CRUD operations for configuration modules
    this.actr.registerCRUD('config-module', {
      create: async (data) => {
        this.moduleRegistry.registerModule(data);
        return data;
      },
      
      read: async (id: string) => {
        return this.moduleRegistry.getModule(id);
      },
      
      update: async (id: string, data) => {
        const module = this.moduleRegistry.getModule(id);
        if (module) {
          const updated = { ...module, ...data };
          this.moduleRegistry.registerModule(updated);
          return updated;
        }
        return null;
      },
      
      delete: async (id: string) => {
        return this.moduleRegistry.unregisterModule(id);
      }
    });
    
    // CRUD operations for configuration settings
    this.actr.registerCRUD('config-setting', {
      create: async (data) => {
        this.settingsHierarchy.setSetting(data.path, data.value, data.level, data.viewId, data.datasetId);
        return { path: data.path, value: data.value, level: data.level };
      },
      
      read: async (path: string) => {
        return this.settingsHierarchy.getSetting(path);
      },
      
      update: async (path: string, data) => {
        this.settingsHierarchy.setSetting(path, data.value, data.level, data.viewId, data.datasetId);
        return { path, value: data.value, level: data.level };
      },
      
      delete: async (path: string) => {
        // Delete by setting to undefined
        this.settingsHierarchy.setSetting(path, undefined);
        return { path };
      }
    });
    */
  }
  
  /**
   * Register Configgr with Interfacr
   * Routes UI operations through Interfacr instead of direct package usage
   */
  private registerWithInterfacr(): void {
    if (!this.interfacr) return;
    
    // Register as handler for preferences/settings UI
    this.interfacr.registerHandler('preferences', {
      show: (params: any) => this.showPreferences(params),
      getSetting: (params: any) => this.getSetting(params.path, params.viewId, params.datasetId),
      updateSetting: (params: any) => this.updateSetting(params.path, params.value, params.level, params.viewId, params.datasetId),
    });
  }
  
  /**
   * Create settings hierarchy with localStorage backend
   */
  private createSettingsHierarchy(storage?: Storage): any {
    const storageKey = `configgr:${this.appId}:settings`;
    const defaultStorage = typeof window !== 'undefined' ? window.localStorage : null;
    const store = storage || defaultStorage;
    
    // Load settings from storage
    const loadSettings = (): any => {
      if (!store) return {};
      try {
        const data = store.getItem(storageKey);
        return data ? JSON.parse(data) : {};
      } catch (error) {
        console.warn('[ConfiggrManager] Failed to load settings:', error);
        return {};
      }
    };
    
    // Save settings to storage
    const saveSettings = (settings: any): void => {
      if (!store) return;
      try {
        store.setItem(storageKey, JSON.stringify(settings));
      } catch (error) {
        console.warn('[ConfiggrManager] Failed to save settings:', error);
      }
    };
    
    let settings = loadSettings();
    
    return {
      getSetting: (path: string, viewId?: string | null, datasetId?: string | null): any => {
        return settings[path];
      },
      
      setSetting: (path: string, value: any, level?: string, viewId?: string | null, datasetId?: string | null): void => {
        settings[path] = value;
        saveSettings(settings);
      },
      
      deleteSetting: (path: string): void => {
        delete settings[path];
        saveSettings(settings);
      },
      
      getAllSettings: (): any => {
        return { ...settings };
      },
      
      clearSettings: (): void => {
        settings = {};
        saveSettings(settings);
      }
    };
  }
  
  /**
   * Show preferences UI through Interfacr
   * Applies profile perspective to UI
   */
  private async showPreferences(params: any): Promise<void> {
    // Get active profile if Profylr available
    let profileContext = null;
    if (this.profylr) {
      try {
        const profile = await this.profylr.getActiveProfile();
        if (profile?.perspective) {
          profileContext = {
            tone: profile.perspective.tone,
            style: profile.perspective.style,
            language: profile.perspective.language,
          };
        }
      } catch (error) {
        console.warn('Failed to get profile perspective:', error);
      }
    }
    
    // TODO: Implement preferences UI display with profile context
    // This would show the PreferencesManager component with adapted UI
    console.log('Show preferences with profile context:', profileContext);
  }
  
  // ==================== Module Management ====================
  
  /**
   * Register a configuration module
   */
  registerModule(moduleConfig): void {
    this.moduleRegistry.registerModule(moduleConfig);
    
    if (this.loggr) {
      this.loggr.log('debug', `Registered config module: ${moduleConfig.id}`);
    }
  }
  
  /**
   * Get a module
   */
  getModule(id: string): any {
    return this.moduleRegistry.getModule(id);
  }
  
  /**
   * Get all modules
   */
  getAllModules(): any[] {
    return this.moduleRegistry.getAllModules();
  }
  
  /**
   * Get modules by category
   */
  getModulesByCategory(category: string): any[] {
    return this.moduleRegistry.getModulesByCategory(category);
  }
  
  /**
   * Get grouped modules
   */
  getGroupedModules(): any {
    return this.moduleRegistry.getGroupedModules();
  }
  
  // ==================== Settings Management ====================
  
  /**
   * Get active profile from Profylr
   */
  async getActiveProfile(): Promise<any> {
    if (!this.profylr) {
      return null;
    }
    
    try {
      return await this.profylr.getActiveProfile();
    } catch (error) {
      console.warn('[Configgr] Failed to get active profile:', error);
      return null;
    }
  }
  
  /**
   * Get a setting with hierarchical fallback
   * PROFYLR INTEGRATION: Checks active profile settings first
   */
  async getSettingAsync(path: string, viewId?: string | null, datasetId?: string | null): Promise<any> {
    // 1. Check active profile settings (highest priority)
    if (this.profylr) {
      const profile = await this.getActiveProfile();
      if (profile?.settings && profile.settings[path] !== undefined) {
        return profile.settings[path];
      }
    }
    
    // 2. Check hierarchy (global → view → dataset)
    const value = this.settingsHierarchy.getSetting(path, viewId, datasetId);
    if (value !== undefined) {
      return value;
    }
    
    // 3. Try cache if available
    if (this.cachr) {
      const cacheKey = `configgr:${this.appId}:${path}`;
      return this.cachr.get(cacheKey);
    }
    
    return undefined;
  }
  
  /**
   * Get a setting synchronously (legacy method)
   * Note: Does not check profile settings
   */
  getSetting(path: string, viewId?: string | null, datasetId?: string | null): any {
    const value = this.settingsHierarchy.getSetting(path, viewId, datasetId);
    
    // Try cache if available
    if (this.cachr && value === undefined) {
      const cacheKey = `configgr:${this.appId}:${path}`;
      return this.cachr.get(cacheKey);
    }
    
    return value;
  }
  
  /**
   * Update a setting
   * PROFYLR INTEGRATION: Can store in active profile if level='profile'
   */
  async updateSettingAsync(path: string, value: any, level: string = 'global', viewId?: string | null, datasetId?: string | null): Promise<void> {
    // Store in active profile if level is 'profile'
    if (level === 'profile' && this.profylr) {
      const profile = await this.getActiveProfile();
      if (profile) {
        try {
          await this.profylr.update(profile.id, {
            settings: {
              ...profile.settings,
              [path]: value,
            },
          });
          
          if (this.loggr) {
            this.loggr.log('info', `Updated profile setting: ${path} = ${value}`);
          }
          return;
        } catch (error) {
          console.error('[Configgr] Failed to update profile setting:', error);
        }
      }
    }
    
    // Otherwise use hierarchy
    this.settingsHierarchy.setSetting(path, value, level, viewId, datasetId);
    
    // Cache if available
    if (this.cachr) {
      const cacheKey = `configgr:${this.appId}:${path}`;
      this.cachr.set(cacheKey, value);
    }
    
    if (this.loggr) {
      this.loggr.log('info', `Updated setting: ${path} = ${value} (${level})`);
    }
  }
  
  /**
   * Update a setting synchronously (legacy method)
   * Note: Cannot store in profile
   */
  updateSetting(path: string, value: any, level: string = 'global', viewId?: string | null, datasetId?: string | null): void {
    this.settingsHierarchy.setSetting(path, value, level, viewId, datasetId);
    
    // Cache if available
    if (this.cachr) {
      const cacheKey = `configgr:${this.appId}:${path}`;
      this.cachr.set(cacheKey, value);
    }
    
    if (this.loggr) {
      this.loggr.log('info', `Updated setting: ${path} = ${value} (${level})`);
    }
  }
  
  /**
   * Reset settings
   */
  resetSettings(level: string = 'global', viewId?: string | null, datasetId?: string | null): void {
    switch (level) {
      case 'dataset':
        if (viewId && datasetId) {
          this.settingsHierarchy.clearDatasetSettings(viewId, datasetId);
        }
        break;
      case 'view':
        if (viewId) {
          this.settingsHierarchy.clearViewSettings(viewId);
        }
        break;
      case 'global':
      default:
        this.settingsHierarchy.resetAll();
        break;
    }
    
    if (this.loggr) {
      this.loggr.log('info', `Reset ${level} settings`);
    }
  }
  
  // ==================== Export/Import ====================
  
  /**
   * Export settings
   * Uses Actr to orchestrate with Outputtr if available
   */
  async exportSettings(): Promise<any> {
    const settings = this.settingsHierarchy.exportSettings();
    
    // If Actr is available, use it to orchestrate export
    if (this.actr) {
      try {
        // Try to use Outputtr for file generation if available
        const outputtrAvailable = this.actr.hasAction('outputtr:generate-file');
        
        if (outputtrAvailable) {
          const result = await this.actr.execute('outputtr:generate-file', {
            data: settings,
            format: 'json',
            filename: `configgr-settings-${Date.now()}.json`,
            options: {
              pretty: true,
              indent: 2
            }
          });
          
          if (this.loggr) {
            this.loggr.log('info', 'Settings exported via Outputtr');
          }
          
          return result;
        }
      } catch (error) {
        if (this.loggr) {
          this.loggr.log('warn', 'Outputtr export failed, falling back to direct export', error);
        }
      }
    }
    
    // Fallback to direct export
    if (this.loggr) {
      this.loggr.log('info', 'Settings exported (direct)');
    }
    
    return settings;
  }
  
  /**
   * Import settings
   * Uses Actr to orchestrate with Fylr if available
   */
  async importSettings(settings: any): Promise<void> {
    // If settings is a File object and Actr is available, use Fylr to read it
    if (this.actr && settings instanceof File) {
      try {
        const fylrAvailable = this.actr.hasAction('fylr:read-file');
        
        if (fylrAvailable) {
          const result = await this.actr.execute('fylr:read-file', {
            file: settings,
            format: 'json'
          });
          
          settings = result.data;
          
          if (this.loggr) {
            this.loggr.log('info', 'Settings file read via Fylr');
          }
        }
      } catch (error) {
        if (this.loggr) {
          this.loggr.log('warn', 'Fylr import failed, expecting parsed settings object', error);
        }
        throw new Error('Failed to read settings file. Please provide parsed settings object.');
      }
    }
    
    // Import the settings
    this.settingsHierarchy.importSettings(settings);
    
    // Clear cache if available
    if (this.cachr) {
      try {
        await this.cachr.clear(`configgr:${this.appId}:*`);
      } catch (error) {
        // Ignore cache clear errors
      }
    }
    
    if (this.loggr) {
      this.loggr.log('info', 'Settings imported successfully');
    }
  }
  
  // ==================== Search ====================
  
  /**
   * Search settings across all modules
   */
  searchSettings(query: string): any[] {
    const results: any[] = [];
    const lowerQuery = query.toLowerCase();
    
    this.moduleRegistry.getAllModules().forEach((module) => {
      if (module.settings) {
        Object.entries(module.settings).forEach(([key, setting]: [string, any]) => {
          const searchText = `${module.name} ${setting.label || ''} ${setting.description || ''}`.toLowerCase();
          if (searchText.includes(lowerQuery)) {
            results.push({
              module,
              setting,
              key: `${module.id}.${key}`
            });
          }
        });
      }
    });
    
    return results;
  }
}

// Singleton instance
let instance: ConfiggrManager | null = null;

/**
 * Get the singleton instance
 */
export function getConfiggr(options?: ConfiggrManagerOptions): ConfiggrManager {
  if (!instance) {
    instance = new ConfiggrManager(options);
  }
  return instance;
}

export default ConfiggrManager;
